import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { apiService } from '../../services/api';
import Pagination from '../Common/Pagination';
import Toast from '../Common/Toast';
import './Support.css';


const STATUS_OPTIONS = [
  ['new', 'Новое'],
  ['in_progress', 'В работе'],
  ['waiting_user', 'Ожидает ответа пользователя'],
  ['resolved', 'Выполнено'],
  ['closed', 'Закрыто'],
];
const PRIORITY_OPTIONS = [
  ['normal', 'Обычный'],
  ['high', 'Высокий'],
  ['critical', 'Критический'],
];

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU');
};

const extractError = (error) => {
  const data = error.response?.data;
  return data?.detail || data?.text?.[0] || data?.attachments?.[0] || error.message;
};

const SecureAttachment = ({ attachment, onPreview }) => {
  const [objectUrl, setObjectUrl] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    let url = '';
    apiService.downloadSupportAttachment(attachment.id)
      .then((response) => {
        if (!active) return;
        url = URL.createObjectURL(response.data);
        setObjectUrl(url);
      })
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.id]);

  if (failed) return <span className="support-attachment support-attachment--error">Вложение недоступно</span>;
  return (
    <button type="button" className="support-attachment" onClick={() => objectUrl && onPreview({ attachment, objectUrl })} title="Открыть изображение">
      {objectUrl ? <img src={objectUrl} alt={attachment.original_name} /> : <span>Загрузка...</span>}
      <span className="support-attachment__name">{attachment.original_name}</span>
    </button>
  );
};

const AttachmentPreview = ({ preview, onClose }) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => document.removeEventListener('keydown', handleEscape, { capture: true });
  }, [onClose]);

  const download = () => {
    const link = document.createElement('a');
    link.href = preview.objectUrl;
    link.download = preview.attachment.original_name || `attachment-${preview.attachment.id}`;
    link.click();
  };

  return createPortal(
    <div className="support-preview-overlay" role="dialog" aria-modal="true" aria-label="Предпросмотр скриншота" onClick={onClose}>
      <div className="support-preview" onClick={(event) => event.stopPropagation()}>
        <div className="support-preview__header">
          <strong>{preview.attachment.original_name}</strong>
          <div>
            <button type="button" className="support-preview__download" onClick={download}>Скачать</button>
            <button type="button" className="support-preview__close" onClick={onClose} aria-label="Закрыть предпросмотр">&times;</button>
          </div>
        </div>
        <div className="support-preview__body">
          <img src={preview.objectUrl} alt={preview.attachment.original_name} />
        </div>
      </div>
    </div>,
    document.body,
  );
};

const SupportPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = !!user?.is_staff || !!user?.is_superuser || String(user?.role || '').toLowerCase() === 'admin';
  const replyFileRef = useRef(null);
  const [requests, setRequests] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  const [reply, setReply] = useState('');
  const [replyFiles, setReplyFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      const all = [];
      let page = 1;
      while (true) {
        const response = await apiService.getSupportRequests(`?page=${page}&page_size=96`);
        const chunk = response.data?.results || response.data || [];
        all.push(...chunk);
        if (!response.data?.next || page >= 200) break;
        page += 1;
      }
      setRequests(all);
      const requestedId = Number(new URLSearchParams(location.search).get('request'));
      setSelectedId((current) => {
        if (requestedId && all.some((item) => item.id === requestedId)) return requestedId;
        if (current && all.some((item) => item.id === current)) return current;
        return all[0]?.id || null;
      });
    } catch (error) {
      setToast({ type: 'error', message: `Не удалось загрузить обращения: ${extractError(error)}` });
    } finally {
      setLoading(false);
    }
  }, [location.search]);

  const loadDetail = useCallback(async (id) => {
    if (!id) {
      setDetail(null);
      return;
    }
    try {
      setDetailLoading(true);
      const response = await apiService.getSupportRequest(id);
      setDetail(response.data);
      await apiService.markSupportRequestRead(id).catch(() => {});
      window.dispatchEvent(new Event('aot-support-updated'));
      setRequests((items) => items.map((item) => item.id === id ? { ...item, unread_count: 0 } : item));
    } catch (error) {
      setToast({ type: 'error', message: `Не удалось открыть обращение: ${extractError(error)}` });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);
  useEffect(() => { loadDetail(selectedId); }, [loadDetail, selectedId]);
  useEffect(() => {
    if (!isAdmin) return;
    apiService.getUsers().then((response) => {
      const list = response.data?.results || response.data || [];
      setAdmins(list.filter((item) => item.is_staff || item.is_superuser || item.role === 'admin'));
    }).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    const handler = () => loadRequests();
    window.addEventListener('aot-support-updated', handler);
    return () => window.removeEventListener('aot-support-updated', handler);
  }, [loadRequests]);
  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests.filter((item) => {
      if (statusFilter && item.status !== statusFilter) return false;
      if (!query) return true;
      const haystack = `${item.id} ${item.subject} ${item.author_name} ${item.author_email} ${item.last_message_preview}`.toLowerCase();
      return haystack.includes(query);
    });
  }, [requests, search, statusFilter]);

  useEffect(() => setCurrentPage(1), [search, statusFilter, itemsPerPage]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));
  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const refreshSelected = async () => {
    await Promise.all([loadRequests(), loadDetail(selectedId)]);
  };

  const updateTicket = async (changes) => {
    try {
      const response = await apiService.updateSupportRequest(selectedId, changes);
      setDetail(response.data);
      setToast({ type: 'success', message: 'Обращение обновлено.' });
      await loadRequests();
    } catch (error) {
      setToast({ type: 'error', message: `Не удалось обновить обращение: ${extractError(error)}` });
    }
  };

  const addReplyFiles = (incoming) => {
    try {
      const next = [...replyFiles];
      for (const file of Array.from(incoming || [])) {
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new Error('Поддерживаются PNG, JPEG и WebP.');
        if (file.size > 10 * 1024 * 1024) throw new Error('Размер файла не должен превышать 10 МБ.');
        if (next.length >= 3) throw new Error('Можно приложить не более трех изображений.');
        next.push(file);
      }
      setReplyFiles(next);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!reply.trim()) return;
    const payload = new FormData();
    payload.append('text', reply.trim());
    replyFiles.forEach((file) => payload.append('attachments', file));
    setSending(true);
    try {
      await apiService.addSupportMessage(selectedId, payload);
      setReply('');
      setReplyFiles([]);
      setToast({ type: 'success', message: 'Сообщение отправлено.' });
      await refreshSelected();
      window.dispatchEvent(new Event('aot-support-updated'));
    } catch (error) {
      setToast({ type: 'error', message: `Не удалось отправить сообщение: ${extractError(error)}` });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="support-page">
      <header className="support-page__header">
        <div>
          <p className="support-eyebrow">Обратная связь</p>
          <h1>{isAdmin ? 'Техническая поддержка' : 'Мои обращения'}</h1>
          <p>{isAdmin ? 'Запросы пользователей, переписка и контроль выполнения.' : 'Следите за статусом запросов и отвечайте администратору.'}</p>
        </div>
        <button type="button" className="support-new-button" onClick={() => window.dispatchEvent(new Event('aot-open-support'))}>+ Новое обращение</button>
      </header>

      <div className="support-toolbar">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по теме, сообщению или пользователю" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">Все статусы</option>
          {STATUS_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
        </select>
        <span>Найдено: <strong>{filtered.length}</strong></span>
      </div>

      <div className="support-workspace">
        <section className="support-request-list" aria-label="Список обращений">
          {loading ? <div className="support-empty">Загрузка...</div> : paginated.length === 0 ? (
            <div className="support-empty">Обращений не найдено</div>
          ) : paginated.map((item) => (
            <button
              type="button"
              key={item.id}
              className={`support-request-card${selectedId === item.id ? ' is-active' : ''}${item.unread_count ? ' is-unread' : ''}`}
              onClick={() => setSelectedId(item.id)}
            >
              <span className="support-request-card__top">
                <span>#{item.id}</span>
                <span className={`support-priority support-priority--${item.priority}`}>{item.priority_label}</span>
              </span>
              <strong>{item.subject}</strong>
              {isAdmin && <span className="support-request-card__author">{item.author_name || item.author_email}</span>}
              <span className="support-request-card__preview">{item.last_message_preview}</span>
              <span className="support-request-card__bottom">
                <span className={`support-status support-status--${item.status}`}>{item.status_label}</span>
                <time>{formatDateTime(item.last_message_at)}</time>
                {item.unread_count > 0 && <b className="support-unread-count">{item.unread_count}</b>}
              </span>
            </button>
          ))}
          {filtered.length > 0 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} totalItems={filtered.length} onItemsPerPageChange={setItemsPerPage} />
          )}
        </section>

        <section className="support-thread" aria-label="Переписка по обращению">
          {!selectedId ? <div className="support-empty support-empty--thread">Выберите обращение</div> : detailLoading && !detail ? (
            <div className="support-empty support-empty--thread">Загрузка переписки...</div>
          ) : detail ? (
            <>
              <div className="support-thread__header">
                <div>
                  <span className="support-thread__number">Обращение #{detail.id}</span>
                  <h2>{detail.subject}</h2>
                  <div className="support-thread__meta">
                    <span>{detail.category_label}</span>
                    <span>{detail.author_name} · {detail.author_email}</span>
                    <span>{formatDateTime(detail.created_at)}</span>
                  </div>
                </div>
                <span className={`support-status support-status--${detail.status}`}>{detail.status_label}</span>
              </div>

              {isAdmin && (
                <div className="support-admin-controls">
                  <label>Статус<select value={detail.status} onChange={(e) => updateTicket({ status: e.target.value })}>{STATUS_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                  <label>Приоритет<select value={detail.priority} onChange={(e) => updateTicket({ priority: e.target.value })}>{PRIORITY_OPTIONS.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
                  <label>Ответственный<select value={detail.assigned_to || ''} onChange={(e) => updateTicket({ assigned_to: e.target.value || null })}><option value="">Не назначен</option>{admins.map((admin) => <option value={admin.id} key={admin.id}>{admin.full_name || admin.email}</option>)}</select></label>
                </div>
              )}

              {(detail.page_url || detail.browser_info?.user_agent) && (
                <details className="support-context">
                  <summary>Технический контекст</summary>
                  {detail.page_url && <div><strong>Страница:</strong> <a href={detail.page_url} target="_blank" rel="noreferrer">{detail.page_url}</a></div>}
                  {detail.browser_info?.user_agent && <div><strong>Браузер:</strong> {detail.browser_info.user_agent}</div>}
                  {detail.browser_info?.viewport && <div><strong>Окно:</strong> {detail.browser_info.viewport.width} × {detail.browser_info.viewport.height}</div>}
                </details>
              )}

              <div className="support-messages">
                {(detail.messages || []).map((message) => (
                  <article className={`support-message ${message.is_admin_message ? 'support-message--admin' : 'support-message--user'}`} key={message.id}>
                    <div className="support-message__head">
                      <strong>{message.is_admin_message ? 'Техподдержка' : (message.author_name || message.author_email)}</strong>
                      <time>{formatDateTime(message.created_at)}</time>
                    </div>
                    <div className="support-message__text">{message.text}</div>
                    {message.attachments?.length > 0 && <div className="support-attachments">{message.attachments.map((attachment) => <SecureAttachment attachment={attachment} onPreview={setPreview} key={attachment.id} />)}</div>}
                  </article>
                ))}
              </div>

              <form className="support-reply" onSubmit={sendReply} onPaste={(event) => {
                const images = Array.from(event.clipboardData?.files || []).filter((file) => file.type.startsWith('image/'));
                if (images.length) { event.preventDefault(); addReplyFiles(images); }
              }}>
                <textarea rows={4} maxLength={10000} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Введите ответ... Можно вставить скриншот из буфера." />
                {replyFiles.length > 0 && <div className="support-file-list">{replyFiles.map((file, index) => <div className="support-file-chip" key={`${file.name}-${index}`}><span>{file.name}</span><button type="button" onClick={() => setReplyFiles(replyFiles.filter((_, itemIndex) => itemIndex !== index))}>&times;</button></div>)}</div>}
                <div className="support-reply__actions">
                  <input ref={replyFileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple hidden onChange={(e) => { addReplyFiles(e.target.files); e.target.value = ''; }} />
                  <button type="button" className="btn btn-secondary" onClick={() => replyFileRef.current?.click()}>Приложить скриншот</button>
                  <button type="submit" className="btn btn-primary" disabled={sending || !reply.trim()}>{sending ? 'Отправка...' : 'Отправить ответ'}</button>
                </div>
              </form>
            </>
          ) : null}
        </section>
      </div>
      {preview && <AttachmentPreview preview={preview} onClose={() => setPreview(null)} />}
      <Toast toast={toast} />
    </div>
  );
};

export default SupportPage;
